// ─── Reading the days-to-go number off a dashboard's source tile ─────────────
// PURE module (no React, no fetch) so it can be tested against real Looker
// result shapes.
//
// THE BUG THIS EXISTS FOR (2026-09-08). A KUNYE dashboard whose event is 60 days
// away showed "⏳ 305 days ago". 305 + 60 = 365: it was counting to the PRIOR
// EDITION of the same event. The dashboard carries both — CURRENT EVENT
// (KUNYE | JOZI XI) and PAST EVENT (KUNYE | JOZI IX), locked side by side — and
// the reader took `data[0]`'s first numeric field with no idea that a
// current-vs-past tile answers for two events at once. Whichever event the query
// happened to sort or pivot first won, and on that dashboard it was last year's.
//
// The rest of the codebase already learned this. server/tileValues.js reads a
// goal's tile with `matchPivotKey(pivots, currentEventValue(locks))` and says
// why: "read THIS event's column specifically — identified by the suite's
// Current Event lock — instead of the latest/biggest pivot (which can be a prior
// edition)". The countdown never got the same treatment. This is that rule, for
// this reader, in the one place both the row pick and the column pick happen.
//
// Nothing here guesses which edition is "current" from dates or magnitudes: it
// matches the event the DASHBOARD says is current, and where that cannot be
// matched it falls back to exactly the old behaviour, so a single-value tile —
// the documented contract for this setting — is unaffected.

import { splitValues } from './filterValues.js';

const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();

// WHICH event this dashboard is about, from its own resolved filter values.
// Same rule as server/tileValues.currentEventValue: a filter named "Current
// Event" (any spacing/case), else a plain "Event Name". Decoded through the lock
// codec, because a bare split(',')[0] returns half of an event whose title
// contains a comma — with the `^` escape still attached — and that half matches
// no column at all.
export function currentEventOf(filterValues = {}) {
  const keys = Object.keys(filterValues || {});
  const k = keys.find((x) => /current\s*event/i.test(x)) || keys.find((x) => /^\s*event\s*name\s*$/i.test(x));
  if (!k) return '';
  return splitValues(filterValues[k])[0] || '';
}

// A pivoted cell is keyed by pivot value rather than carrying `.value` itself.
// Prefer the current event's column; otherwise the LAST one, which is what every
// other reader here defaults to (client SingleValueTile.resolvePivotCell and
// server resolvePivotCellSrv both walk the keys backwards).
function cellFor(cell, pivots, preferKey) {
  if (!cell || cell.value !== undefined || cell.rendered !== undefined) return cell;
  if (preferKey && cell[preferKey] && (cell[preferKey].value != null || (cell[preferKey].rendered != null && cell[preferKey].rendered !== ''))) return cell[preferKey];
  const keys = (pivots && pivots.length) ? pivots.map((p) => p.key) : Object.keys(cell);
  for (let i = keys.length - 1; i >= 0; i--) {
    const c = cell[keys[i]];
    if (c && (c.value != null || (c.rendered != null && c.rendered !== ''))) return c;
  }
  return cell[keys[keys.length - 1]] || null;
}

// The pivot key equal to `val`, or null (server matchPivotKey).
function pivotKeyFor(pivots, val) {
  if (!val || !Array.isArray(pivots)) return null;
  const hit = pivots.find((pv) => norm(pv.key) === norm(val));
  return hit ? hit.key : null;
}

// WHICH ROW is the current event's, when the tile answers with one row per event
// (a comparison tile that is not pivoted). Any dimension holding the event name
// identifies it. -1 when nothing does — kept distinct from "it is row 0", so
// "matched the first row" is never confused with "matched nothing at all".
function rowIndexFor(rows, dimensions, event) {
  if (!event) return -1;
  return rows.findIndex((r) => (dimensions || []).some((d) => {
    const c = r[d.name];
    return c && (norm(c.value) === norm(event) || norm(c.rendered) === norm(event));
  }));
}

const numOf = (cell) => {
  if (!cell) return null;
  const v = cell.value != null && cell.value !== '' ? cell.value : cell.rendered;
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/[\s,]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
};

// THE NEXT OCCURRENCE, when several numbers come back and NOTHING says which
// event each belongs to. The KUNYE tile is exactly this: one field in use
// ("Days Before Event"), two rows — -305 and 60 — and no event column to match
// on, because two events in the warehouse answer to the same name a year apart.
// Row order is then the only thing separating them, which is how the countdown
// came to be a year out.
//
// A COUNTDOWN has one honest reading here and it needs no event label: the next
// occurrence. The soonest one still ahead of us (smallest value >= 0), or, when
// they have all been and gone, the most recent (the negative closest to zero).
// That is what a person reading "-305, 60" says without hesitating, and it is
// the same answer the current-event match gives when a label IS available — this
// only decides cases the old code decided by row order.
function nextOccurrence(values) {
  const ahead = values.filter((n) => n >= 0);
  return ahead.length ? Math.min(...ahead) : Math.max(...values);
}

// The days-to-go number a source tile is showing FOR THIS EVENT.
// `res` is a Looker json_detail result; `currentEvent` the value from
// currentEventOf(). Returns null when there is no number to read.
export function daysToGoFrom(res, currentEvent = '') {
  const rows = res?.data || [];
  if (!rows.length) return null;
  const dims = res.fields?.dimensions || [];
  const pivots = res.pivots || [];
  const key = pivotKeyFor(pivots, currentEvent);
  // Measures and table calcs first — a numeric DIMENSION is a last resort, and
  // on a comparison tile it is often the event's year or id rather than a count.
  const fields = [...(res.fields?.measures || []), ...(res.fields?.table_calculations || []), ...dims];
  const idx = rowIndexFor(rows, dims, currentEvent);
  for (const f of fields) {
    // 1. A row or column CARRIES the event the dashboard names — follow it,
    //    whatever it does to the number. That is the answer with a source, and
    //    it is why naming the past event honestly reads the past event.
    if (idx >= 0) {
      const n = numOf(cellFor(rows[idx][f.name], pivots, key));
      if (n != null) return n;
      continue;
    }
    const values = rows.map((r) => numOf(cellFor(r[f.name], pivots, key))).filter((n) => n != null);
    if (!values.length) continue;
    // 2. A pivot column matched, or there is only one number: nothing to choose.
    if (key || values.length === 1) return values[0];
    // 3. Several numbers and nothing says which event each is. Read the
    //    countdown as a countdown rather than trusting row order.
    return nextOccurrence(values);
  }
  return null;
}
